// WHAT: Tab component for displaying daily work plan with matched tasks
// WHY:  Provides overview of planned work chunks with task associations and progress
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: MatchedChunkTask type, workspace config for colors
// RISKS:
//   - NOTE: Score color mapping is hardcoded - may not match design system
//   - NOTE: Deadline formatting may not handle all date formats
//   - NOTE: Progress calculation may not account for partial completion
//   - NOTE: Workspace config prop is typed as any - may cause type issues

import type { MatchedChunkTask } from "@/domains/productivity/timetracq/hooks/useDailyPlan";
import type { FC } from "react";

import { Clock, Calendar, Target, Briefcase } from "lucide-react";

import { Card } from "@/components/ui";

/**
 * Props for DailyPlanTab component
 */
interface Properties {
  /** Daily capacity in hours */
  dailyCapacity?: number;
  /** Daily plan entries with matched tasks */
  dailyPlan: MatchedChunkTask[];
  /** Source of shortfall data */
  shortfallSource?: string;
  /** Hours worked today */
  workedToday?: number;
  /** Workspace configuration for colors */
  workspaceConfig?: Record<string, Record<string, { color: string; hours: number }>>;
}

/**
 * Gets aspect color from workspace config.
 *
 * @param workspace - Workspace name
 *
 * @param aspect - Aspect name
 *
 * @param workspaceConfig - Workspace configuration
 *
 * @returns Color string
 *
 */
const getAspectColor = (
  workspace: string,
  aspect: string,
  workspaceConfig: Record<string, Record<string, { color: string; hours: number }>>,
): string => {
  if (!workspaceConfig[workspace]?.[aspect]) {
    return "#64748b"; // NOTE: Fallback to slate-500
  }
  return workspaceConfig[workspace][aspect].color || "#64748b";
};

/**
 * Gets badge color class for task score.
 *
 * @param score - Task score
 *
 * @returns CSS class string
 *
 */
const getScoreBadge = (score: null | number): string => {
  if (!score) {
    return "bg-slate-500/20 text-slate-400";
  }
  if (score >= 80) {
    return "bg-green-500/20 text-green-400";
  }
  if (score >= 60) {
    return "bg-yellow-500/20 text-yellow-400";
  }
  if (score >= 40) {
    return "bg-tracq-deep-orange/20 text-tracq-deep-orange";
  }
  return "bg-red-500/20 text-red-400";
};

/**
 * Formats deadline date for display.
 *
 * @param deadline - Deadline date string
 *
 * @returns Formatted date string
 *
 */
const formatDeadline = (deadline: null | string): string => {
  if (!deadline) {
    return "—";
  }
  try {
    const date = new Date(deadline);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Heute";
    }
    if (diffDays === 1) {
      return "Morgen";
    }
    if (diffDays < 0) {
      return `${String(Math.abs(diffDays))} Tage überfällig`;
    }
    if (diffDays <= 7) {
      return `${String(diffDays)} Tage`;
    }

    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return deadline;
  }
};

/**
 * Gets deadline color class based on deadline date.
 *
 * @param deadline - Deadline date string
 *
 * @returns CSS class string
 *
 */
const getDeadlineColor = (deadline: null | string): string => {
  if (!deadline) {
    return "text-slate-400";
  }
  try {
    const date = new Date(deadline);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return "text-red-400";
    }
    if (diffDays <= 1) {
      return "text-tracq-deep-orange";
    }
    if (diffDays <= 3) {
      return "text-yellow-400";
    }
    return "text-green-400";
  } catch {
    return "text-slate-400";
  }
};

/**
 * WHAT:
 *   Tab component that displays daily work plan with planned chunks, matched
 *   tasks, progress indicators, and capacity information.
 *
 * WHY:
 *   Enables users to see their planned work for the day with task associations
 *   and track progress against capacity.
 *
 * PROPS:
 *   - dailyPlan: MatchedChunkTask[] — Daily plan entries
 *   - workedToday?: number — Hours worked today
 *   - dailyCapacity?: number — Daily capacity in hours
 *   - shortfallSource?: string — Source of shortfall data
 *   - workspaceConfig?: Record<string, Record<string, { color: string; hours: number }>> — Workspace config for colors
 *
 * BEHAVIOR:
 *   - Displays planned chunks with task information
 *   - Shows progress indicators
 *   - Displays capacity and worked hours
 *   - Colors aspects based on workspace config
 *   - Shows task scores and deadlines
 *
 * RISKS:
 *   - NOTE: Score color mapping is hardcoded - may not match design system
 *   - NOTE: Deadline formatting may not handle all date formats
 *   - NOTE: Progress calculation may not account for partial completion
 *   - NOTE: Workspace config prop is typed as any - may cause type issues
 *
 * @param root0
 *
 * @param root0.dailyPlan
 *
 * @param root0.workedToday
 *
 * @param root0.dailyCapacity
 *
 * @param root0.shortfallSource
 *
 * @param root0.workspaceConfig
 *
 * @returns JSX.Element
 *
 */
export const DailyPlanTab: FC<Properties> = ({
  dailyCapacity = 0,
  dailyPlan,
  shortfallSource,
  workedToday = 0,
  workspaceConfig = {},
}) => {
  const totalPlannedHours = dailyPlan.reduce((sum, entry) => sum + entry.use_hours, 0);
  const totalHours = totalPlannedHours + workedToday;

  if (dailyPlan.length === 0) {
    return (
      <Card
        className="flex flex-col items-center justify-center h-64 text-center bg-slate-700/30 border-slate-600/50"
        padding="lg"
        variant="elevated"
      >
        <div className="w-16 h-16 mb-4 rounded-full bg-slate-600/50 flex items-center justify-center">
          <Calendar className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Kein Plan für heute</h3>
        <p className="text-slate-400 max-w-md">
          Es sind keine Aufgaben für heute geplant. Erstelle Tasks oder überprüfe deine Shortfalls.
        </p>
      </Card>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header mit Statistiken - Neues Layout */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="space-y-6">
          {/* Titel und Info */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-tracq-bright-blue/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-tracq-bright-blue" />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-white">Daily Plan</h3>
              <p className="text-slate-400">Dein Arbeitsplan für heute</p>
              {shortfallSource ? (
                <p className="text-slate-500 text-sm mt-1">
                  📊 Basierend auf:{" "}
                  {shortfallSource === "monthly (fallback)" ? "Monatsziel (Fallback)" : "Bis heute"}
                </p>
              ) : null}
            </div>
          </div>

          {/* Statistiken Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tracq-bright-blue/20 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-tracq-bright-blue" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Geplant</div>
                  <div className="text-xl font-bold text-tracq-bright-blue">
                    {totalPlannedHours.toFixed(1)}h
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-500/20 flex items-center justify-center">
                  <Target className="w-4 h-4 text-slate-300" />
                </div>
                <div>
                  <div className="text-sm text-slate-400">Aufgaben</div>
                  <div className="text-xl font-bold text-white">{dailyPlan.length}</div>
                </div>
              </div>
            </div>

            {workedToday > 0 && (
              <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Gearbeitet</div>
                    <div className="text-xl font-bold text-green-400">
                      {workedToday.toFixed(1)}h
                    </div>
                  </div>
                </div>
              </div>
            )}

            {dailyCapacity > 0 && (
              <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-tracq-deep-orange/20 flex items-center justify-center">
                    <Target className="w-4 h-4 text-tracq-deep-orange" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Kapazität</div>
                    <div className="text-xl font-bold text-white">
                      {totalHours.toFixed(1)}h / {dailyCapacity.toFixed(1)}h
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Task Liste - Verbessertes Layout */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {dailyPlan.map((entry) => (
            <Card
              className="group bg-slate-600/30 hover:bg-slate-600/50 transition-all duration-200 border-slate-500/50 hover:border-slate-400/50 hover:scale-[1.01]"
              key={`${entry.parent}-${entry.child}-${entry.task_id ?? entry.task_name ?? "no-task"}`}
              variant="default"
            >
              <div className="p-6">
                {/* Header mit Zeit und Score */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-500/30 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-xl font-bold text-white">{entry.parent}</h4>
                        {/* Kombinierter Workspace + Aspect Badge */}
                        <span
                          className="px-3 py-1 rounded-lg text-xs font-medium border flex items-center gap-1"
                          style={{
                            backgroundColor: `${getAspectColor(entry.parent, entry.child, workspaceConfig)}20`,
                            borderColor: `${getAspectColor(entry.parent, entry.child, workspaceConfig)}40`,
                            color: getAspectColor(entry.parent, entry.child, workspaceConfig),
                          }}
                        >
                          <span className="font-semibold">{entry.parent}</span>
                          <span className="text-[10px] opacity-70">•</span>
                          <span>{entry.child}</span>
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm">{entry.child}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Zeit */}
                    <div className="bg-tracq-bright-blue/10 border border-tracq-bright-blue/20 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-tracq-bright-blue" />
                        <span className="font-bold text-tracq-bright-blue">
                          {entry.use_hours.toFixed(1)}h
                        </span>
                      </div>
                    </div>

                    {/* Score */}
                    <div className={`px-3 py-2 rounded-lg border ${getScoreBadge(entry.score)}`}>
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        <span className="font-bold text-sm">{entry.score ?? "–"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Task Details */}
                <div className="space-y-3">
                  {/* Task Name */}
                  <div className="bg-slate-700/40 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-200 font-medium">
                        {entry.task_name ?? "Keine spezifische Aufgabe"}
                      </span>
                    </div>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Deadline */}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className={`text-sm font-medium ${getDeadlineColor(entry.deadline)}`}>
                          {formatDeadline(entry.deadline)}
                        </span>
                      </div>
                    </div>

                    {/* Shortfall Before */}
                    {entry.shortfall_before > 0 && (
                      <div className="bg-tracq-deep-orange/10 border border-tracq-deep-orange/30 rounded-lg px-3 py-1">
                        <span className="text-sm font-medium text-tracq-deep-orange">
                          Rückstand: {entry.shortfall_before.toFixed(1)}h
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Footer Zusammenfassung - Verbessertes Design */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200"
        variant="default"
      >
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tracq-bright-blue/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-tracq-bright-blue" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Gesamt geplant</div>
                <div className="text-lg font-bold text-white">{totalHours.toFixed(1)} Stunden</div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm text-slate-400">Aufgaben</div>
              <div className="text-lg font-bold text-tracq-bright-blue">
                {dailyPlan.length} Tasks
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
